import i18n from "./index";

/**
 * Bound to the active language, not `nl-NL`: a hardcoded locale is a translation hiding in a
 * formatter. Cached per locale, because `Intl.DateTimeFormat` is expensive and these run per row.
 */
const cache = new Map<string, Intl.DateTimeFormat>();

function formatter(name: string, options: Intl.DateTimeFormatOptions) {
  const key = `${i18n.language}:${name}`;
  let found = cache.get(key);
  if (!found) {
    found = new Intl.DateTimeFormat(i18n.language, options);
    cache.set(key, found);
  }
  return found;
}

const toDate = (value: Date | string) => (value instanceof Date ? value : new Date(value));

/** A deadline: "1 maart 2027 17:00". */
export const moment = (value: Date | string) =>
  formatter("moment", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(toDate(value));

/** A wedstrijddag named in full: "zaterdag 12 juni 2027". */
export const datumLang = (value: Date | string) =>
  formatter("lang", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(toDate(value));

/** A date in a table cell, where the row already carries the year: "12 jun". */
export const datumKort = (value: Date | string) =>
  formatter("kort", { day: "numeric", month: "short" }).format(toDate(value));

/**
 * A `yyyy-MM-dd` from the API as a local `Date`. Built field by field rather than through
 * `new Date(iso)`, which parses a date-only string as UTC midnight - a day early everywhere
 * west of Greenwich, and one hour from being a day early here.
 */
function vanIso(iso: string) {
  const [jaar, maand, dag] = iso.split("-").map(Number);
  return jaar && maand && dag ? new Date(jaar, maand - 1, dag) : undefined;
}

/** `3 november 2027` - the huisstijl's long form, for a heading. Echoes an unparseable date. */
export const datumIsoLang = (iso: string) => {
  const date = vanIso(iso);
  return date
    ? formatter("isoLang", { day: "numeric", month: "long", year: "numeric" }).format(date)
    : iso;
};

/** `03-11-2027` - for a table column, where the long form would not line up. */
export const datumIsoKort = (iso: string) => {
  const date = vanIso(iso);
  return date
    ? formatter("isoKort", { day: "2-digit", month: "2-digit", year: "numeric" }).format(date)
    : iso;
};

/** A date with its clock time, for an audit line: "3-11-2027 14:05". */
export const datumTijd = (value: Date | string) =>
  formatter("datumTijd", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(toDate(value));
